/**
 * Sector scenarios (the coordinator's row, Oct 10; decision 121's content stage): a curated, seeded sector the owner
 * plays instead of a random one — named systems with a shape, one enemy commander with a doctrine, garrisons that
 * differ, a stated goal and a day limit, and the rules the scenario needs on (for it only). Built over the ordinary
 * generator (`createSector`), then rewritten: positions, names, worlds, lanes, holdings, structures, squads and
 * kits. The scenario rides on the state (`SectorState.scenario`), so a save carries it. Pure.
 */
import { createSector, DEFAULT_RULES } from './sector';
import { initialKnowledge, observe } from './knowledge';
import { kitPreset, type KitPresetKey } from '../core/unitModifiers';
import type { PlanetArchetype, PlanetSizePreset } from '../types';
import type { FactionId, Lane, SectorRules, SectorState, SectorStructureKind, SystemId } from './types';
import type { CommanderStyle } from './sectorAi';

export interface ScenarioSystem {
  id: SystemId; name: string; x: number; y: number;
  archetype: PlanetArchetype; planetCount: number; planetSize: PlanetSizePreset; hazards: boolean;
  owner: FactionId | null; garrisoned: boolean; structures: SectorStructureKind[];
  /** One line for the brief: what this place is. */
  role: string;
}

export interface ScenarioSquad { faction: FactionId; at: SystemId; units: number; kit: KitPresetKey }

export interface ScenarioDef {
  id: string;
  title: string;
  seed: string;
  /** The brief, shown on day 1: a few short paragraphs. */
  brief: string[];
  /** The goal in one line, shown every day. */
  goal: string;
  /** Who plays (the human) and who the stand-in commands. */
  player: FactionId;
  enemy: FactionId;
  enemyStyle: CommanderStyle;
  enemyDoctrine: { personality: string; caution: number };
  /** Win: hold this system. Lose: lose your home, or reach the day limit first. */
  target: SystemId;
  dayLimit: number;
  systems: ScenarioSystem[];
  lanes: Array<[SystemId, SystemId, number]>;
  squads: ScenarioSquad[];
  supply: Record<FactionId, number>;
  /** The rules the scenario needs (on for it only; the switchboard shows them). */
  rules: Partial<SectorRules>;
}

/** What a scenario leaves on the state (a save carries it). */
export interface ScenarioState {
  id: string;
  title: string;
  goal: string;
  brief: string[];
  player: FactionId;
  enemy: FactionId;
  enemyStyle: CommanderStyle;
  target: SystemId;
  dayLimit: number;
  /** Set once decided. */
  result?: 'won' | 'lost';
  roles: Record<SystemId, string>;
}

/** The first scenario: the Ister gap. Six systems, one stronghold, a back way in fog, a prize on the flank. */
export const FIRST_SCENARIO: ScenarioDef = {
  id: 'first',
  title: 'The Ister Gap',
  seed: 'scenario-first',
  goal: 'Take Faro, the Red stronghold, before day 30. Keep Anvil.',
  brief: [
    'Red holds the far end of the gap: Faro, its stronghold, with a sensor that watches two lanes out, and Dorsa, a forward post on the short road.',
    'You hold Anvil with two squads and a depot. Cinder, the middle, is open ground and Red wants it too. Bracken, north of you, is worth taking for its intact planets. Erebus, south, is the long way round, and fog often hides it.',
    'Scout before you commit: what you know of a place is only as fresh as your last look. Refit at a rest point (home or a depot). The forecast on each system says when fog, storm or dust will hide an approach.',
  ],
  player: 'blue',
  enemy: 'red',
  enemyStyle: 'hold',   // keeps Faro and Dorsa, strikes only next door (62.42 #1)
  enemyDoctrine: { personality: 'defensive', caution: 0.6 },
  target: 'faro',
  dayLimit: 30,
  systems: [
    { id: 'anvil', name: 'Anvil', x: 130, y: 300, archetype: 'thalia', planetCount: 5, planetSize: 'mixed', hazards: false, owner: 'blue', garrisoned: true, structures: ['depot'], role: 'your home: two squads and a depot' },
    { id: 'bracken', name: 'Bracken', x: 330, y: 120, archetype: 'pelago', planetCount: 7, planetSize: 'large', hazards: false, owner: null, garrisoned: false, structures: [], role: 'a prize: seven large, intact worlds — worth taking' },
    { id: 'cinder', name: 'Cinder', x: 480, y: 310, archetype: 'ochrus', planetCount: 5, planetSize: 'mixed', hazards: true, owner: null, garrisoned: false, structures: [], role: 'the middle: open ground, dust, and both of you want it' },
    { id: 'dorsa', name: 'Dorsa', x: 680, y: 170, archetype: 'pallor', planetCount: 4, planetSize: 'small', hazards: false, owner: 'red', garrisoned: true, structures: ['antenna'], role: 'Red’s forward post: a light scout squad and an antenna on the short road' },
    { id: 'erebus', name: 'Erebus', x: 680, y: 460, archetype: 'bruma', planetCount: 5, planetSize: 'mixed', hazards: false, owner: null, garrisoned: false, structures: [], role: 'the back way: longer lanes, fog more days than not' },
    { id: 'faro', name: 'Faro', x: 880, y: 310, archetype: 'tephra', planetCount: 6, planetSize: 'mixed', hazards: true, owner: 'red', garrisoned: true, structures: ['sensor', 'depot'], role: 'the goal: Red’s stronghold, entrenched, with a sensor and a depot' },
  ],
  lanes: [
    ['anvil', 'bracken', 2], ['anvil', 'cinder', 2], ['bracken', 'cinder', 1.5], ['bracken', 'dorsa', 2.5],
    ['cinder', 'dorsa', 1.5], ['cinder', 'erebus', 2], ['dorsa', 'faro', 1.5], ['erebus', 'faro', 2.5],
  ],
  squads: [
    { faction: 'blue', at: 'anvil', units: 3, kit: 'standard' },
    { faction: 'blue', at: 'anvil', units: 3, kit: 'standard' },
    // Red holds (the hold style): the stronghold's entrenched garrison and a reserve, and the forward scouts; musters add one every five days
    { faction: 'red', at: 'faro', units: 3, kit: 'entrenched' },
    { faction: 'red', at: 'faro', units: 3, kit: 'standard' },
    { faction: 'red', at: 'dorsa', units: 2, kit: 'scout' },
  ],
  supply: { blue: 40, red: 30 },
  rules: {
    // what this scenario is about: the weather and its forecast, garrisons that learn, a prize worth taking, the traces a fight leaves
    weatherWindows: true, adaptiveGarrisons: true, valueSystems: true, signatures: true, salvage: true,
    // the screen's economy, a little leaner
    reinforceDays: 5, incomePerSystemPerDay: 4, healPerDay: 15,
  },
};

export const SCENARIOS: Record<string, ScenarioDef> = { first: FIRST_SCENARIO };

/** Build a scenario's sector. */
export function createScenario(def: ScenarioDef, seed: string = def.seed, extraRules: Partial<SectorRules> = {}): SectorState {
  const perFaction = Math.max(...[def.player, def.enemy].map(f => def.squads.filter(q => q.faction === f).length));
  const base = createSector(seed, {
    systems: def.systems.length,
    squadsPerFaction: perFaction,
    unitsPerSquad: 3,
    startingSupply: 0,
    factions: [
      { id: def.player, name: 'Blue', color: '#4a90d9' },
      { id: def.enemy, name: 'Red', color: '#d9534f', doctrine: { personality: def.enemyDoctrine.personality, caution: def.enemyDoctrine.caution } },
    ],
    rules: { ...def.rules, ...extraRules },
  });
  // the map: our systems over the generated ones (keeping each generated map seed by index)
  const systems = def.systems.map((d, i) => ({
    ...base.systems[i],
    id: d.id, name: d.name, x: d.x, y: d.y,
    archetype: d.archetype, planetCount: d.planetCount, planetSize: d.planetSize, hazardDensity: (d.hazards ? 'some' : 'none') as 'some' | 'none',
    owner: d.owner, garrisoned: d.garrisoned,
    structures: d.structures.map((kind, k) => ({ id: i * 10 + k + 1, kind, planetIndex: 0, surfaceDir: { x: 0, y: 1, z: 0 }, builtDay: 0, builtBy: d.owner ?? def.player })),
  }));
  const lanes: Lane[] = def.lanes.map(([a, b, days]) => ({ a, b, days }));
  const homeOf = (f: FactionId) => def.systems.find(s => s.owner === f && s.structures.includes('depot') && s.garrisoned && (f === def.player ? s.id !== def.target : s.id === def.target))!.id;
  const factions = base.factions.map(f => ({ ...f, home: homeOf(f.id), supply: def.supply[f.id] ?? 0 }));
  // squads: the generated ones re-placed and refitted; extras dropped
  const counters: Record<string, number> = {};
  const squads = def.squads.map(sq => {
    const n = (counters[sq.faction] = (counters[sq.faction] ?? 0) + 1);
    const g = base.squads.filter(q => q.faction === sq.faction)[n - 1];
    const kit = kitPreset(sq.kit);
    return { ...g, at: sq.at, units: g.units.slice(0, sq.units).map(u => ({ ...u, kit: { ...kit, launcher: { ...kit.launcher }, suit: { ...kit.suit }, equipment: [...kit.equipment], traits: { ...kit.traits } } })) };
  });
  const scenario: ScenarioState = {
    id: def.id, title: def.title, goal: def.goal, brief: def.brief, player: def.player, enemy: def.enemy, enemyStyle: def.enemyStyle,
    target: def.target, dayLimit: def.dayLimit, roles: Object.fromEntries(def.systems.map(s => [s.id, s.role])),
  };
  const s0: SectorState = { ...base, systems, lanes, factions, squads, scenario, knowledge: {}, log: [], orderLog: [], options: { ...base.options, scenario: def.id } };
  const knowledge: SectorState['knowledge'] = {};
  for (const f of factions) knowledge[f.id] = initialKnowledge(s0, f.id);
  // the brief names every holding, so both sides start knowing who holds what (not what stands there)
  for (const f of factions) for (const sys of systems) if (sys.owner && sys.owner !== f.id) knowledge[f.id].systems[sys.id] = { systemId: sys.id, owner: sys.owner, garrisoned: sys.garrisoned, seenDay: 0 };
  return observe({ ...s0, knowledge, log: [{ day: 0, kind: 'order', text: `${def.title}: ${def.goal}` }] });
}

/**
 * The scenario's result, once decided: won when the player holds the target; lost when the player's home falls or
 * the day limit passes first. Null while it runs (and for a sector with no scenario).
 */
export function scenarioResult(state: Pick<SectorState, 'scenario' | 'systems' | 'factions' | 'day'>): 'won' | 'lost' | null {
  const sc = state.scenario;
  if (!sc) return null;
  if (sc.result) return sc.result;
  const owner = (id: SystemId) => state.systems.find(s => s.id === id)?.owner ?? null;
  if (owner(sc.target) === sc.player) return 'won';
  const home = state.factions.find(f => f.id === sc.player)?.home;
  if (home && owner(home) !== sc.player) return 'lost';
  if (state.day >= sc.dayLimit - 1e-9) return 'lost';
  return null;
}

/** Days left on the scenario's clock (null without one). */
export function scenarioDaysLeft(state: Pick<SectorState, 'scenario' | 'day'>): number | null {
  return state.scenario ? Math.max(0, state.scenario.dayLimit - state.day) : null;
}

/** The scenario's rules, as the switchboard reads them (the defaults with the scenario's on top). */
export function scenarioRules(def: ScenarioDef): SectorRules {
  return { ...DEFAULT_RULES, ...def.rules } as SectorRules;
}
